/**
 * SuggestedQuestionsPanel - AI-suggested follow-up question cards.
 *
 * Shows a flat list of suggestion cards. Clicking a card marks it as asked and
 * moves it to the "Asked" list. The × button dismisses a card silently.
 * Each card optionally shows a muted group tag (the AI-assigned section category).
 *
 * AI logic lives in useSuggestedQuestions; this component is purely presentational.
 */

import type { ExtractSection } from '../hooks/useDealExtract';
import type { SuggestedQuestion } from '../types/live-meeting';

export interface SuggestedQuestionsPanelProps {
  activeSuggestions: SuggestedQuestion[];
  askedSuggestions: SuggestedQuestion[];
  /** Called when the user clicks a suggestion card to mark it as asked. */
  onAsk: (id: string) => void;
  /** Called when the user clicks the × button to silently dismiss a card. */
  onDismiss: (id: string) => void;
  /**
   * Live coaching sections from useDealCoaching - used to show whether the
   * answer for an asked suggestion has actually been captured.
   */
  sections?: ExtractSection[];
  /** True while an AI suggest call is in-flight. */
  isInferring?: boolean;
  /** When true, no deal is linked - show a prompt instead of suggestions. */
  noDeal?: boolean;
}

function isCaptured(suggestion: SuggestedQuestion, sections: ExtractSection[]): boolean {
  if (!suggestion.fieldKey) return true; // greeting / unmatched - don't flag
  for (const section of sections) {
    const field = section.groups
      .flatMap((q) => q.fields)
      .find((f) => f.fieldKey === suggestion.fieldKey);
    if (field) return field.state.status === 'answered';
  }
  return false;
}

export function SuggestedQuestionsPanel({
  activeSuggestions,
  askedSuggestions,
  onAsk,
  onDismiss,
  sections = [],
  noDeal = false,
}: SuggestedQuestionsPanelProps) {
  if (noDeal) {
    return (
      <div className="lm-suggestions__panel-body">
        <p className="lm-suggestions__empty">Link a deal to see suggested questions.</p>
      </div>
    );
  }

  const hasActive = activeSuggestions.length > 0;
  const hasAsked = askedSuggestions.length > 0;

  if (!hasActive && !hasAsked) {
    return (
      <div className="lm-suggestions__panel-body">
        <p className="lm-suggestions__empty">
          Suggested questions will appear as the conversation progresses.
        </p>
      </div>
    );
  }

  return (
    <div className="lm-suggestions__panel-body">
      {hasActive && (
        <div className="lm-suggestions__active" aria-label="Suggested questions">
          {activeSuggestions.map((suggestion) => (
            <div key={suggestion.id} className="lm-suggestions__card-row">
              <button
                type="button"
                className="lm-suggestions__card"
                onClick={() => onAsk(suggestion.id)}
                aria-label={`Mark as asked: ${suggestion.text}`}
              >
                <span className="lm-suggestions__card-text">{suggestion.text}</span>
                {suggestion.group && (
                  <span className="lm-suggestions__card-tag" aria-hidden="true">
                    {suggestion.group}
                  </span>
                )}
              </button>
              <button
                type="button"
                className="lm-suggestions__card-dismiss"
                onClick={() => onDismiss(suggestion.id)}
                aria-label="Dismiss suggestion"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {hasAsked && (
        <div className="lm-suggestions__asked">
          <p className="lm-suggestions__asked-label">Asked</p>
          <ul className="lm-suggestions__asked-list" aria-label="Asked questions">
            {askedSuggestions.map((suggestion) => {
              const captured = isCaptured(suggestion, sections);
              return (
                <li key={suggestion.id} className="lm-suggestions__asked-item">
                  <span
                    className={`lm-suggestions__asked-icon lm-suggestions__asked-icon--${captured ? 'captured' : 'pending'}`}
                    aria-label={captured ? 'Captured' : 'Not yet captured'}
                    aria-hidden="true"
                  >
                    {captured ? '✓' : '○'}
                  </span>
                  <span className="lm-suggestions__asked-text">{suggestion.text}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
